import React, { useEffect, useState } from 'react';

interface User {
    id: number;
    username: string;
    role: string;
    phone: string;
    email: string;
    security_code: string;
    created_at: string;
}

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);

    // Create State
    const [newUser, setNewUser] = useState({ username: '', password: '', role: 'staff', phone: '', email: '' });

    // Edit State
    const [editingUser, setEditingUser] = useState<Partial<User> & { password?: string } | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/admin/users', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) setUsers(await response.json());
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const response = await fetch('/api/admin/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newUser)
            });
            if (response.ok) {
                setMessage('사용자가 생성되었습니다.');
                setNewUser({ username: '', password: '', role: 'staff', phone: '', email: '' });
                fetchUsers();
            } else {
                setMessage('생성 실패');
            }
        } catch (err) {
            setMessage('오류 발생');
        }
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser || !editingUser.id) return;

        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`/api/admin/users/${editingUser.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editingUser)
            });
            if (response.ok) {
                alert('사용자 정보가 수정되었습니다.');
                setIsEditModalOpen(false);
                setEditingUser(null);
                fetchUsers();
            } else {
                alert('수정 실패');
            }
        } catch (err) {
            alert('오류 발생');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        const token = localStorage.getItem('token');
        await fetch(`/api/admin/users/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchUsers();
    };

    const openEditModal = (user: User) => {
        setEditingUser({ ...user, password: '' }); // Password empty by default (only send if changing)
        setIsEditModalOpen(true);
    };

    return (
        <div>
            <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '30px' }}>
                <h4>새 사용자 등록</h4>
                <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                    <label>
                        <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>아이디</span>
                        <input type="text" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required style={{ width: '100%', padding: '8px', border: '1px solid #ddd' }} />
                    </label>
                    <label>
                        <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>비밀번호</span>
                        <input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required style={{ width: '100%', padding: '8px', border: '1px solid #ddd' }} />
                    </label>
                    <label>
                        <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>연락처</span>
                        <input type="text" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} placeholder="010-0000-0000" style={{ width: '100%', padding: '8px', border: '1px solid #ddd' }} />
                    </label>
                    <label>
                        <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>이메일</span>
                        <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@example.com" style={{ width: '100%', padding: '8px', border: '1px solid #ddd' }} />
                    </label>
                    <label>
                        <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>권한</span>
                        <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} style={{ width: '100%', padding: '8px', border: '1px solid #ddd' }}>
                            <option value="staff">직원</option>
                            <option value="admin">관리자</option>
                        </select>
                    </label>
                    <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#D6A32E', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', height: '35px' }}>등록</button>
                </form>
                {message && <div style={{ marginTop: '10px', color: 'green', fontSize: '13px' }}>{message}</div>}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f1f3f5' }}>
                        <th style={{ padding: '10px', textAlign: 'left' }}>ID</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>사용자명</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>연락처</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>이메일</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>권한</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>보안 코드</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>관리</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '10px' }}>{user.id}</td>
                            <td style={{ padding: '10px', fontWeight: 'bold' }}>{user.username}</td>
                            <td style={{ padding: '10px' }}>{user.phone || '-'}</td>
                            <td style={{ padding: '10px' }}>{user.email || '-'}</td>
                            <td style={{ padding: '10px' }}>
                                <span style={{
                                    padding: '3px 8px', borderRadius: '12px', fontSize: '11px',
                                    backgroundColor: user.role === 'admin' ? '#d1e7dd' : '#cfe2ff',
                                    color: user.role === 'admin' ? '#0f5132' : '#084298'
                                }}>
                                    {user.role === 'admin' ? '관리자' : '직원'}
                                </span>
                            </td>
                            <td style={{ padding: '10px', color: '#dc3545', fontWeight: 'bold', fontFamily: 'monospace' }}>{user.security_code}</td>
                            <td style={{ padding: '10px' }}>
                                <button onClick={() => openEditModal(user)} style={{ padding: '4px 8px', marginRight: '5px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>수정</button>
                                {user.username !== 'admin' && (
                                    <button onClick={() => handleDelete(user.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>삭제</button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Edit Modal */}
            {isEditModalOpen && editingUser && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', width: '400px' }}>
                        <h3>사용자 정보 수정</h3>
                        <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <label>
                                사용자명
                                <input type="text" value={editingUser.username} onChange={e => setEditingUser({ ...editingUser, username: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                            </label>
                            <label>
                                새 비밀번호 (변경시에만 입력)
                                <input type="password" value={editingUser.password} onChange={e => setEditingUser({ ...editingUser, password: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '5px' }} placeholder="변경 안함" />
                            </label>
                            <label>
                                연락처
                                <input type="text" value={editingUser.phone || ''} onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                            </label>
                            <label>
                                이메일
                                <input type="text" value={editingUser.email || ''} onChange={e => setEditingUser({ ...editingUser, email: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '5px' }} />
                            </label>
                            <label>
                                권한
                                <select value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })} style={{ width: '100%', padding: '8px', marginTop: '5px' }}>
                                    <option value="staff">직원</option>
                                    <option value="admin">관리자</option>
                                </select>
                            </label>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} style={{ padding: '8px 16px', border: '1px solid #ddd', background: 'white', cursor: 'pointer', borderRadius: '4px' }}>취소</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>저장</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
